import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import moment from "moment";
import { isString, isNil } from "lodash-es";
import { useRect } from "@reach/rect";
import { addMonths, startOfDay, endOfDay } from "date-fns";
import { useEventListener, useId, onEnter } from "../../helpers";
import { Label } from "../label/Label";
import Input from "../../forms/input/Input";
import { CalendarDisplay } from "./CalendarDisplay";
import { inferDate, isValidDateFormat } from "./parseDate";
import { DAY_FORMAT } from "./constants";
import { toDate, getHoursAndMinutes, isWithinRange } from "./utils";
import { TimeSelect } from "./TimeSelect";
export const MAX_OPTIONS_HEIGHT = 254;
const INPUT_HEIGHT = 38;
const BORDER_HEIGHT = 1;
export const MAX_RESULTS_BOX_HEIGHT = MAX_OPTIONS_HEIGHT + INPUT_HEIGHT + BORDER_HEIGHT * 2;
function formatDate(date) {
  return date.format(DAY_FORMAT);
}
function getMinutes(date) {
  return date.getHours() * 60 + date.getMinutes();
}
export function DatePicker(props) {
  const hasDate = moment(props.date).isValid() && !isNil(props.date);
  const innerId = useId();
  const labelId = props.id || `tui-date-picker-${innerId}`;
  const dateInputRef = useRef();
  const calendarRef = useRef();
  const [date, setDate] = useState(() => hasDate ? props.date.toDate() : new Date());
  const [value, setValue] = useState(() => hasDate ? formatDate(props.date) : props.date);
  const [minutes, setMinutes] = useState(() => hasDate ? getMinutes(props.date.toDate()) : 0);
  const [showCalendar, setShowCalendarInternal] = useState(props.alwaysShowCalendar);
  function setShowCalendar(newValue) {
    if (props.alwaysShowCalendar) return;
    setShowCalendarInternal(newValue);
  }
  const [previousValue, setPreviousValue] = React.useState(null);
  const [hasFocus, setHasFocus] = useState(false);
  const minDate = startOfDay(toDate(props.minDate));
  const maxDate = endOfDay(toDate(props.maxDate));
  const rect = useRect(dateInputRef) || {};
  useLayoutEffect(() => {
    if (!props.fixedPosition) return;
    const inputMargin = 4;
    let styles = {
      top: `${rect.top + rect.height + inputMargin}px`,
      left: `${rect.left}px`,
      right: `${rect.right}px`,
      transform: null,
      position: "fixed",
      height: null
    };
    const collidesWithBottom = window.innerHeight <= rect.bottom + MAX_RESULTS_BOX_HEIGHT;
    if (collidesWithBottom) {
      // when options' rect extends past the bottom of the window move it above the input
      styles.transform = `translateY(-100%) translateY(-${rect.height + inputMargin * 2}px)`;
      styles.height = `${MAX_OPTIONS_HEIGHT}px`;
    }
    Object.entries(styles).forEach(_ref => {
      let [key, value] = _ref;
      calendarRef.current.style[key] = value;
    });
  }, [rect.top, rect.left, rect.right, rect.width, rect.height]);
  useEventListener(calendarRef.current, "focusout", event => {
    if (!calendarRef.current.contains(event.relatedTarget)) {
      setShowCalendar(false);
    }
  });
  useEffect(() => {
    setDate(hasDate ? props.date.toDate() : new Date());
    setValue(hasDate ? formatDate(props.date) : props.date);
    setMinutes(hasDate ? getMinutes(props.date.toDate()) : "");
  }, [props.date]);
  function onChange(date) {
    if (date === null) {
      if (props.date !== null) props.onChange(null, false);
      return;
    }
    if (date.isSame(props.date, props.showTime ? "minute" : "day")) return;
    const isOutOfRange = !isWithinRange(toDate(date), toDate(props.minDate), toDate(props.maxDate));
    props.onChange(date, isOutOfRange);
  }
  function updateDate(day) {
    let fromCalendar = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (!day && props.optional && isString(value) && value.trim() === "") {
      onChange(null);
    } else if (fromCalendar || isString(value) && isValidDateFormat(value)) {
      const newDay = day || isString(value) && inferDate(value, props.date.toDate());
      const newDate = moment(newDay);
      newDate.set(getHoursAndMinutes(minutes));
      onChange(newDate);
      setValue(formatDate(moment(newDay)));
      setDate(newDay);
    } else {
      setValue(formatDate(props.date));
      setDate(props.date.toDate());
    }
  }
  return /*#__PURE__*/React.createElement("div", {
    className: `tui-date-picker ${props.alwaysShowCalendar ? "tui-date-picker--inline-cal" : ""}`,
    tabIndex: -1,
    onFocus: () => setHasFocus(true),
    onBlur: () => setHasFocus(false)
  }, props.label && /*#__PURE__*/React.createElement(Label, {
    htmlFor: labelId,
    required: props.required
  }, props.label), /*#__PURE__*/React.createElement("div", {
    className: "tui-date-picker__inputs"
  }, /*#__PURE__*/React.createElement(Input, {
    id: labelId,
    ref: dateInputRef,
    value: isString(value) ? value : "",
    disabled: props.disabled,
    onFocus: () => {
      setShowCalendar(true);
      if (hasDate) setDate(props.date.toDate());
      const input = dateInputRef.current;
      input.setSelectionRange(0, input.value.length);
      if (isString(value)) {
        setPreviousValue(value);
      }
    },
    onBlur: e => {
      if (previousValue) {
        const inferredData = inferDate(value, date);
        const isExceedingMaxDate = maxDate && new Date(inferredData).getTime() > new Date(maxDate).getTime();
        const isSubceedingMinDate = minDate && new Date(inferredData).getTime() < new Date(minDate).getTime();
        if (isExceedingMaxDate || isSubceedingMinDate) {
          setValue(previousValue);
          setDate(inferDate(previousValue, date));
          setPreviousValue(null);
          return;
        }
      }
      if (!calendarRef.current.contains(e.relatedTarget)) {
        setShowCalendar(false);
        props.onInputChange(value);
      }
    },
    onChange: _ref2 => {
      let {
        currentTarget: {
          value
        }
      } = _ref2;
      setValue(value);
      setDate(inferDate(value, date));
    },
    inputProps: {
      onKeyDown: onEnter(() => updateDate()),
      autoComplete: "off"
    },
    error: !hasFocus && props.error
  }), props.showTime && /*#__PURE__*/React.createElement(TimeSelect, {
    disabled: props.disabled,
    date: date,
    value: minutes,
    onChange: minutes => {
      setMinutes(minutes);
      if (hasDate) {
        onChange(props.date.clone().set(getHoursAndMinutes(minutes)));
      }
    }
  })), /*#__PURE__*/React.createElement(CalendarDisplay, {
    ref: calendarRef,
    show: showCalendar,
    selectedDate: hasDate ? props.date.toDate() : new Date(),
    date: date,
    minDate: minDate,
    maxDate: maxDate,
    onChange: date => {
      updateDate(date, true);
      setShowCalendar(false);
    },
    prevMonth: () => setDate(addMonths(date, -1)),
    nextMonth: () => setDate(addMonths(date, 1)),
    alwaysShowCalendar: props.alwaysShowCalendar
  }));
}
DatePicker.defaultProps = {
  alwaysShowCalendar: false,
  fixedPosition: false,
  onInputChange: () => {
    // do nothing
  }
};
DatePicker.TimeSelect = TimeSelect;
//# sourceMappingURL=DatePicker.js.map